import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { Action, CAM, AuditReport } from "../constants/interfaces";
import _ from "lodash";
import api from "../utils/api";

interface State {
  cams: CAM[];
  report: AuditReport;
  error: Error | null;
  loading: boolean;
}

export function getInitialState(): State {
  return {
    cams: [],
    report: {} as AuditReport,
    loading: false,
    error: null
  };
}

export const camTypes = {
  GET_CAMS_REQUEST: "GET_CAMS_REQUEST",
  GET_CAMS_RESPONSE: "GET_CAMS_RESPONSE",
  GET_CAM_REQUEST: "GET_CAM_REQUEST",
  GET_CAM_RESPONSE: "GET_CAM_RESPONSE",
  UPDATE_CAM_REQUEST: "UPDATE_CAM_REQUEST",
  UPDATE_CAM_RESPONSE: "UPDATE_CAM_RESPONSE",
  UPLOAD_FILE_REQUEST: "UPLOAD_FILE_REQUEST",
  UPLOAD_FILE_RESPONSE: "UPLOAD_FILE_RESPONSE",
  REMOVE_FILE_REQUEST: "REMOVE_FILE_REQUEST",
  REMOVE_FILE_RESPONSE: "REMOVE_FILE_RESPONSE",
  GET_AUDIT_CAMS_REQUEST: "GET_AUDIT_CAMS_REQUEST",
  GET_AUDIT_CAMS_RESPONSE: "GET_AUDIT_CAMS_RESPONSE"
};

export const camActions: any = {
  getCams: () => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({ type: camTypes.GET_CAMS_REQUEST });
    try {
      const { data: cams } = await api.get("/cam/getCams");

      dispatch({
        type: camTypes.GET_CAMS_RESPONSE,
        payload: cams,
        error: false
      });
    } catch (e) {
      dispatch({ type: camTypes.GET_CAMS_RESPONSE, payload: e, error: true });
      return e;
    }
  },

  getCamsForAuditHistory: (id: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: camTypes.GET_AUDIT_CAMS_REQUEST });
    try {
      const { data: report } = await api.get(
        `/audits/getAuditHistoryCAMStatusReport/${id}`
      );

      dispatch({
        type: camTypes.GET_AUDIT_CAMS_RESPONSE,
        payload: report,
        error: false
      });
    } catch (e) {
      dispatch({
        type: camTypes.GET_AUDIT_CAMS_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },
  getCam: (id: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: camTypes.GET_CAM_REQUEST });
    try {
      const { data: cam } = await api.get(`/cam/getcam/${id}`);

      dispatch({
        type: camTypes.GET_CAM_RESPONSE,
        payload: cam,
        error: false
      });
    } catch (e) {
      dispatch({ type: camTypes.GET_CAM_RESPONSE, payload: e, error: true });
      return e;
    }
  },

  updateCam: (cam: CAM, postUpdateCallBack?: () => void) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: camTypes.UPDATE_CAM_REQUEST });
    try {
      await api.put(`/cam/updateCam/${cam.caseId}`, cam);
      dispatch({
        type: camTypes.UPDATE_CAM_RESPONSE,
        error: false
      });
      if (postUpdateCallBack) postUpdateCallBack();
    } catch (e) {
      dispatch({ type: camTypes.UPDATE_CAM_RESPONSE, payload: e, error: true });
      return e;
    }
  },

  uploadFile: (caseId: string, file: any) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: camTypes.UPLOAD_FILE_REQUEST });
    try {
      const formData: any = new FormData();
      formData.append("uploadfile", file);
      await api.post(`/cam/uploadFile/${caseId}`, formData);
      dispatch({
        type: camTypes.UPLOAD_FILE_RESPONSE,
        error: false
      });
    } catch (e) {
      dispatch({
        type: camTypes.UPLOAD_FILE_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  removeFile: (caseId: string, filename: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: camTypes.REMOVE_FILE_REQUEST });
    try {
      await api.put(`/cam/removeFile/${caseId}`, filename, {
        // For some reason this is defaulting to form-urlencoded
        headers: {
          "Content-Type": "application/json"
        }
      });
      dispatch({
        type: camTypes.REMOVE_FILE_RESPONSE,
        error: false
      });
    } catch (e) {
      dispatch({
        type: camTypes.REMOVE_FILE_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  }
};

export default function cam(state = getInitialState(), action: Action) {
  const { type, payload, error } = action;
  switch (type) {
    case camTypes.GET_CAMS_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.GET_CAMS_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload, cams: state.cams };
      }
      return { ...state, loading: false, error: null, cams: payload };
    }
    case camTypes.GET_AUDIT_CAMS_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.GET_AUDIT_CAMS_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }
      return { ...state, loading: false, error: null, report: payload };
    }

    case camTypes.GET_CAM_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.GET_CAM_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return { ...state, loading: false, error: payload, cams: state.cams };
      }
      // In the case of success, we merge the existing CAM record with retrieved full record
      const newCam: CAM = payload;
      const cams = [...state.cams];
      const existingCamIndex: number = _.findIndex(
        cams,
        (cam: CAM) => cam.caseId === newCam.caseId
      );
      const existingCam = cams[existingCamIndex];
      existingCamIndex === -1
        ? // If the cam doesn't exist in state, add to reducer
          cams.push(newCam)
        : // If the cam already exists, merge new data
          (cams[existingCamIndex] = _.merge(existingCam, newCam));
      return { ...state, loading: false, error: null, cams };
    }

    case camTypes.UPDATE_CAM_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.UPDATE_CAM_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }

      return { ...state, loading: false, error: null };
    }

    case camTypes.UPLOAD_FILE_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.UPLOAD_FILE_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }
      return { ...state, loading: false, error: null };
    }

    case camTypes.REMOVE_FILE_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case camTypes.REMOVE_FILE_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }
      return { ...state, loading: false, error: null };
    }
  }
  return state;
}
